import { axios } from "@/libraries/axios";
import type {
	BasePaginationParamsSchema,
	GetMemberSyncChatParamsSchema,
	GetMyAlertsParamsSchema,
	ParamsGetMembersSchema,
	PostMemberUpdateVersionBodySchema,
} from "@/schemas";
import { BodyUpdateMemberSchema } from "@/schemas";
import type {
	AuthSignInResponse,
	ContactMemberDetailResponse,
	ContactMemberResponse,
	GetMemberAlertsResponse,
	GetMemberChatsResponse,
	MemberProfileResponse,
	MembersSyncChatResponse,
} from "@/types";
import type { AxiosRequestConfig } from "axios";

class MemberService {
	async getMemberProfile() {
		try {
			const res = await axios.get<MemberProfileResponse>(
				"/api/v1/members/profile",
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getMembers(
		params?: ParamsGetMembersSchema,
		config?: AxiosRequestConfig,
	) {
		try {
			const res = await axios.post<ContactMemberResponse>(
				"/api/v1/members/get_members",
				{
					...params,
					limit: params?.limit ?? 25,
				},
				config,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getMember(id: number) {
		try {
			const res = await axios.get<ContactMemberDetailResponse>(
				`/api/v1/members/${id}`,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateMember(payload: BodyUpdateMemberSchema) {
		try {
			const parsedPayload = BodyUpdateMemberSchema.safeParse(payload);
			if (!parsedPayload.success) return Promise.reject(parsedPayload.error);
			const { id, ...user } = parsedPayload.data;
			const res = await axios.post<MemberProfileResponse>(
				`/api/v1/members/${id}`,
				{
					user,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async syncChat(params: GetMemberSyncChatParamsSchema) {
		try {
			const res = await axios.get<MembersSyncChatResponse>(
				"/api/v1/members/sync_chat",
				{
					params,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updateVersion(payload: PostMemberUpdateVersionBodySchema) {
		try {
			const res = await axios.post<AuthSignInResponse>(
				"/api/v1/members/update_version",
				payload,
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async alerts(params: GetMyAlertsParamsSchema) {
		try {
			const res = await axios.get<GetMemberAlertsResponse>(
				"/api/v1/members/my_alerts",
				{
					params,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async chats(params: BasePaginationParamsSchema) {
		try {
			const res = await axios.get<GetMemberChatsResponse>(
				"/api/v1/members/chats",
				{
					params,
				},
			);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async home(config?: AxiosRequestConfig) {
		try {
			const res = await axios.get<
				Record<"active_cases" | "to_do_list", number>
			>("/api/v1/members/home", config);
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}

export const memberService = Object.freeze(new MemberService());
